/**
 * Floating Support Assistant widget — Premium minimalist redesign.
 *
 * Architecture (unchanged):
 *   - FAB at bottom-right (bottom: 88) above the legacy bug-report widget.
 *   - Right-anchored non-modal drawer (mask={false}).
 *   - State in `useSupportAssistantStore` (Zustand). No history persistence.
 *   - Each assistant message has Sim/Não feedback INSIDE the bubble.
 *
 * Rendering:
 *   - User messages: plain text (whitespace preserved).
 *   - Assistant messages: Markdown via react-markdown + remark-gfm.
 *   - Markdown links pointing at `.mp4` URLs are upgraded to inline
 *     <video controls poster=…> players so tutorial videos referenced
 *     by the agent render directly in the bubble.
 *
 * Sizing:
 *   - Default drawer width: 420px (compact).
 *   - Expanded drawer width: 840px (2× — useful when the answer contains
 *     a video, a wide table or long code blocks). Toggle in the header.
 *   - Whenever any completed assistant message exists in the
 *     conversation and the panel is in compact mode, the resize button
 *     pulses prominently with an indigo halo to nudge expansion. The
 *     pulse stops as soon as the user clicks to expand.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Drawer, Input, Tooltip, Typography } from 'antd';
import {
  Sparkles, ThumbsUp, ThumbsDown, X, RefreshCw, Send,
  Maximize2, Minimize2,
} from 'lucide-react';
import { useSupportAssistantStore, type SupportFeedback } from '../stores/useSupportAssistantStore';
import { useSupportAssistantChat } from '../hooks/useSupportAssistantChat';
import { useAuth } from '../contexts/AuthContext';

/**
 * Fora da primeira pintura: `react-markdown` + `remark-gfm` + o realce só
 * descem quando alguém abre o painel de suporte. Ver o cabeçalho de
 * `SupportAssistantMarkdown.tsx` para a medição e o porquê da fronteira ser
 * aqui, e não em `manualChunks`.
 */
const AssistantMarkdown = React.lazy(() => import('./SupportAssistantMarkdown'));

const { Text } = Typography;
const { TextArea } = Input;

// ── Design tokens ───────────────────────────────────────────────────────────────
const BRAND = {
  from: '#6366F1', // indigo-500
  to: '#8B5CF6',   // violet-500
};
const INK = {
  900: '#0F172A',
  700: '#334155',
  500: '#64748B',
  400: '#94A3B8',
  200: '#E2E8F0',
  100: '#F1F5F9',
  50: '#F8FAFC',
};

const WIDTH_COMPACT = 420;
const WIDTH_EXPANDED = 840;

function firstNameFrom(user: { firstName?: string; name?: string; email?: string } | null): string {
  if (!user) return 'você';
  const fn = user.firstName?.trim();
  if (fn) return fn;
  const name = user.name?.trim();
  if (name) return name.split(/\s+/)[0];
  return user.email?.split('@')[0] ?? 'você';
}

// Strip "Fonte: …md#anchor" / "Source:" / "Citação:" lines (with or
// without leading blockquote markers). The manual is internal — paths
// are not useful to end users.
const SOURCE_LINE_RE =
  /^[\s>]*(?:Fonte|Source|Citação)\s*:\s*[A-Za-z0-9_./-]+\.md(?:#[A-Za-z0-9_-]+)?\s*$/gim;

function normaliseMarkdown(text: string): string {
  let out = text;
  out = out.replace(/([.!?:])\s+(\d{1,2}\.\s)/g, '$1\n\n$2');
  out = out.replace(/([^\n])\s+(#{2,3}\s)/g, '$1\n\n$2');
  out = out
    .split('\n')
    .map((line) => {
      const matches = line.match(/\*\*/g);
      if (matches && matches.length % 2 === 1) {
        const idx = line.lastIndexOf('**');
        return line.slice(0, idx) + line.slice(idx + 2);
      }
      return line;
    })
    .join('\n');
  out = out.replace(/\*\*\s*$/u, '');
  return out;
}

function cleanAssistantBody(text: string): string {
  if (!text) return '';
  const stripped = text.replace(SOURCE_LINE_RE, '');
  const normalised = normaliseMarkdown(stripped);
  return normalised.replace(/\n{3,}/g, '\n\n').trim();
}

// ── One-shot keyframes + markdown styles injection ──────────────────────
const STYLE_ID = 'support-assistant-keyframes';
function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes sa-fadein {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes sa-pulse-halo {
      0%, 100% { box-shadow: 0 8px 24px rgba(99,102,241,0.35), 0 0 0 0 rgba(139,92,246,0.45); }
      50%      { box-shadow: 0 8px 24px rgba(99,102,241,0.45), 0 0 0 10px rgba(139,92,246,0); }
    }
    @keyframes sa-dot {
      0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
      40%           { transform: scale(1.0); opacity: 1; }
    }
    /* Attention pulse for the resize button. Strong indigo halo +
       background fill + scale bump so the user can't miss it on a
       compact-mode panel that just received an answer. Stops the
       moment the user expands. */
    @keyframes sa-attn-pulse {
      0%, 100% {
        background: rgba(99,102,241,0.18);
        box-shadow: 0 0 0 0 rgba(99,102,241,0.65),
                    0 4px 14px rgba(99,102,241,0.30);
        transform: scale(1.0);
      }
      50% {
        background: rgba(99,102,241,0.42);
        box-shadow: 0 0 0 14px rgba(99,102,241,0),
                    0 4px 14px rgba(99,102,241,0.45);
        transform: scale(1.08);
      }
    }
    .sa-msg { animation: sa-fadein 180ms ease-out; }
    .sa-fab { animation: sa-pulse-halo 2.6s ease-in-out infinite; }
    .sa-fab:hover { transform: scale(1.06); }
    .sa-chip:hover { background: ${INK[50]}; border-color: ${INK[200]}; }
    .sa-fb-btn:hover { background: ${INK[50]}; }
    .sa-send:hover:not(:disabled) { filter: brightness(1.08); }
    .sa-send:disabled { cursor: not-allowed; opacity: 0.5; }
    .sa-typing span {
      display: inline-block; width: 6px; height: 6px; margin-right: 3px;
      border-radius: 50%; background: ${INK[400]};
      animation: sa-dot 1.2s infinite ease-in-out;
    }
    .sa-typing span:nth-child(2) { animation-delay: 0.15s; }
    .sa-typing span:nth-child(3) { animation-delay: 0.30s; }
    .sa-input-wrap:focus-within {
      border-color: ${BRAND.from};
      box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
    }
    .sa-resize-pulse {
      animation: sa-attn-pulse 1.2s ease-in-out infinite;
      color: ${BRAND.from} !important;
      border: 1px solid ${BRAND.from} !important;
    }

    /* Markdown body inside assistant bubble */
    .sa-md { font-size: 14px; line-height: 1.55; color: ${INK[900]}; }
    .sa-md > *:first-child { margin-top: 0; }
    .sa-md > *:last-child { margin-bottom: 0; }
    .sa-md p { margin: 0 0 8px; }
    .sa-md strong { font-weight: 600; color: ${INK[900]}; }
    .sa-md em { font-style: italic; }
    .sa-md h1, .sa-md h2, .sa-md h3, .sa-md h4 {
      margin: 12px 0 6px;
      font-weight: 600;
      line-height: 1.3;
      color: ${INK[900]};
    }
    .sa-md h1 { font-size: 16px; }
    .sa-md h2 { font-size: 15px; }
    .sa-md h3, .sa-md h4 { font-size: 14px; }
    .sa-md ul, .sa-md ol { margin: 4px 0 10px; padding-left: 22px; }
    .sa-md li { margin: 3px 0; }
    .sa-md li > p { margin: 0; }
    .sa-md li::marker { color: ${INK[500]}; }
    .sa-md ul ul, .sa-md ol ol, .sa-md ul ol, .sa-md ol ul { margin: 2px 0; }
    .sa-md a { color: ${BRAND.from}; text-decoration: underline; text-underline-offset: 2px; }
    .sa-md a:hover { color: ${BRAND.to}; }
    .sa-md code {
      font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
      font-size: 12.5px;
      background: ${INK[100]};
      color: ${INK[700]};
      padding: 1px 5px;
      border-radius: 4px;
    }
    .sa-md pre {
      margin: 8px 0;
      padding: 10px 12px;
      background: ${INK[100]};
      border: 1px solid ${INK[200]};
      border-radius: 8px;
      overflow-x: auto;
      font-size: 12.5px;
      line-height: 1.5;
    }
    .sa-md pre code { background: transparent; padding: 0; font-size: 12.5px; }
    .sa-md blockquote {
      margin: 8px 0;
      padding: 6px 12px;
      border-left: 3px solid ${INK[200]};
      color: ${INK[500]};
      font-size: 13px;
    }
    .sa-md blockquote p { margin: 0; }
    .sa-md hr { border: 0; border-top: 1px solid ${INK[200]}; margin: 12px 0; }
    .sa-md table {
      border-collapse: collapse;
      margin: 8px 0;
      font-size: 13px;
      width: 100%;
    }
    .sa-md th, .sa-md td {
      border: 1px solid ${INK[200]};
      padding: 6px 8px;
      text-align: left;
    }
    .sa-md th { background: ${INK[50]}; font-weight: 600; }

    /* Inline video player rendered from .mp4 markdown links */
    .sa-video-wrap {
      margin: 10px 0;
      border-radius: 12px;
      overflow: hidden;
      border: 1px solid ${INK[200]};
      background: #000;
      box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 6px 16px rgba(15,23,42,0.08);
    }
    .sa-video-wrap video {
      display: block;
      width: 100%;
      max-height: 480px;
      background: #000;
    }
    .sa-video-caption {
      padding: 6px 12px 8px;
      font-size: 11px;
      color: ${INK[500]};
      background: ${INK[50]};
      border-top: 1px solid ${INK[200]};
    }
  `;
  document.head.appendChild(el);
}

// ── Feedback row (inside bubble) ───────────────────────────────────────────
const FeedbackRow: React.FC<{
  value: SupportFeedback;
  onSelect: (v: SupportFeedback) => void;
}> = ({ value, onSelect }) => {
  const divider = {
    height: 1,
    background: INK[200],
    margin: '12px -2px 10px',
  } as const;

  if (value === 'helpful' || value === 'not_helpful') {
    return (
      <>
        <div style={divider} />
        <div style={{ fontSize: 11, color: INK[500] }}>
          {value === 'helpful' ? 'Obrigado pelo feedback!' : 'Obrigado — vou aprender com isso.'}
        </div>
      </>
    );
  }
  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 10px',
    border: `1px solid ${INK[200]}`,
    borderRadius: 999,
    background: '#FFFFFF',
    color: INK[700],
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1,
    transition: 'background 120ms ease, border-color 120ms ease',
  };
  return (
    <>
      <div style={divider} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, color: INK[500] }}>Essa resposta te ajudou?</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <Tooltip title="Sim, ajudou">
            <button
              type="button"
              className="sa-fb-btn"
              onClick={() => onSelect('helpful')}
              aria-label="Sim, a resposta ajudou"
              style={btnBase}
            >
              <ThumbsUp size={13} /> Sim
            </button>
          </Tooltip>
          <Tooltip title="Não, não resolveu">
            <button
              type="button"
              className="sa-fb-btn"
              onClick={() => onSelect('not_helpful')}
              aria-label="Não, a resposta não ajudou"
              style={btnBase}
            >
              <ThumbsDown size={13} /> Não
            </button>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

// ── Typing indicator ─────────────────────────────────────────────────────
const TypingDots: React.FC = () => (
  <span className="sa-typing" aria-label="Digitando">
    <span /> <span /> <span />
  </span>
);

// ── Message bubble ───────────────────────────────────────────────────────
const MessageBubble: React.FC<{
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  refusalType?: string | null;
  feedback?: SupportFeedback;
  onFeedback?: (value: SupportFeedback) => void;
}> = ({ messageId: _mid, role, content, isStreaming, refusalType, feedback, onFeedback }) => {
  const isUser = role === 'user';

  const body = useMemo(
    () => (isUser ? content : cleanAssistantBody(content)),
    [content, isUser],
  );

  const showFeedback = !isUser && !isStreaming && !!body && !!onFeedback;

  const bubbleStyle: React.CSSProperties = isUser
    ? {
        maxWidth: '85%',
        padding: '10px 14px',
        borderRadius: '16px 16px 4px 16px',
        background: `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)`,
        color: '#FFFFFF',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        lineHeight: 1.55,
        fontSize: 14,
        boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 2px 8px rgba(99,102,241,0.18)',
      }
    : {
        maxWidth: '92%',
        padding: '12px 14px',
        borderRadius: '16px 16px 16px 4px',
        background: '#FFFFFF',
        color: INK[900],
        wordBreak: 'break-word',
        fontSize: 14,
        border: `1px solid ${INK[200]}`,
        boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)',
      };

  return (
    <div
      className="sa-msg"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 14,
      }}
    >
      <div style={bubbleStyle}>
        {isUser ? (
          body ? <span>{body}</span> : null
        ) : body ? (
          <React.Suspense fallback={null}>
            <AssistantMarkdown text={body} />
          </React.Suspense>
        ) : isStreaming ? (
          <TypingDots />
        ) : null}

        {!isUser && refusalType && refusalType !== 'kb_miss' ? (
          <div style={{ marginTop: 8, fontSize: 11, color: INK[400] }}>
            <em>refusal: {refusalType}</em>
          </div>
        ) : null}

        {showFeedback ? (
          <FeedbackRow value={feedback ?? null} onSelect={onFeedback!} />
        ) : null}
      </div>
    </div>
  );
};

// ── Suggestion chip ────────────────────────────────────────────────────────
const SuggestionChip: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    className="sa-chip"
    onClick={onClick}
    style={{
      textAlign: 'left',
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: `1px solid ${INK[200]}`,
      background: '#FFFFFF',
      color: INK[700],
      fontSize: 13,
      cursor: 'pointer',
      transition: 'background 120ms ease, border-color 120ms ease',
    }}
  >
    {label}
  </button>
);

// ── Main widget ──────────────────────────────────────────────────────────────
const SupportAssistantWidget: React.FC = () => {
  const auth = useAuth();
  const isOpen = useSupportAssistantStore((s) => s.isOpen);
  const open = useSupportAssistantStore((s) => s.open);
  const close = useSupportAssistantStore((s) => s.close);
  const resetConversation = useSupportAssistantStore((s) => s.resetConversation);
  const messages = useSupportAssistantStore((s) => s.messages);
  const errorMessage = useSupportAssistantStore((s) => s.errorMessage);

  const { send, submitFeedback, isSending } = useSupportAssistantChat();
  const [draft, setDraft] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<{ focus: () => void } | null>(null);

  useEffect(() => {
    ensureKeyframes();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        useSupportAssistantStore.getState().toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  // When opened via a hand-off from the Janus Agent widget, seed the
  // composer with the forwarded question so the user can review + send.
  useEffect(() => {
    if (!isOpen) return;
    const seed = useSupportAssistantStore.getState().consumePrefill();
    if (seed) {
      setDraft(seed);
      // Focus the composer so the user can immediately press Enter.
      setTimeout(() => inputRef.current?.focus?.(), 0);
    }
  }, [isOpen]);

  const firstName = useMemo(() => firstNameFrom(auth?.user ?? null), [auth?.user]);

  const onSendText = useCallback(
    async (text: string) => {
      const t = text.trim();
      if (!t) return;
      setDraft('');
      await send(t);
    },
    [send],
  );

  const onSend = useCallback(() => onSendText(draft), [draft, onSendText]);

  const onKeyDownInput = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSend();
    }
  };

  const SUGGESTIONS = [
    'Como eu crio um prospect?',
    'Como criar um Deal?',
    'Como configuro o meu ICP?',
    'O contato foi revelado mas não veio telefone nem e-mail. Por quê?',
  ];

  // Pulse the resize button whenever the assistant has produced ANY
  // completed response and the panel is still in compact mode. The
  // pulse calls attention to the option of expanding to 2× width,
  // which is especially useful for replies that contain code blocks,
  // tables or the inline video player. It stops the moment the user
  // expands.
  const hasAssistantReply = useMemo(
    () => messages.some((m) => m.role === 'assistant' && !m.isStreaming && !!m.content),
    [messages],
  );
  const shouldPulseResize = hasAssistantReply && !isExpanded;

  if (!auth?.user) return null;

  const drawerWidth = isExpanded
    ? Math.min(WIDTH_EXPANDED, Math.max(WIDTH_COMPACT, window.innerWidth - 40))
    : WIDTH_COMPACT;

  return (
    <>
      {/* FAB */}
      <Tooltip title="Assistente Janusos.ai  (Ctrl + /)" placement="left">
        <button
          type="button"
          className="sa-fab"
          aria-label="Abrir Assistente Janusos.ai"
          onClick={open}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 88,
            width: 56,
            height: 56,
            borderRadius: 28,
            border: 'none',
            background: `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)`,
            color: '#FFFFFF',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'transform 150ms ease',
          }}
        >
          <Sparkles size={24} strokeWidth={2.25} />
        </button>
      </Tooltip>

      <Drawer
        title={null}
        placement="right"
        width={drawerWidth}
        open={isOpen}
        onClose={close}
        mask={false}
        closable={false}
        headerStyle={{ display: 'none' }}
        styles={{
          body: { display: 'flex', flexDirection: 'column', padding: 0, background: INK[50] },
          wrapper: { boxShadow: '-12px 0 32px rgba(15,23,42,0.10)', transition: 'width 200ms ease' },
        }}
      >
        {/* Custom header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: '#FFFFFF',
            borderBottom: `1px solid ${INK[200]}`,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              background: `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(99,102,241,0.30)',
              flexShrink: 0,
            }}
          >
            <Sparkles size={18} strokeWidth={2.4} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: INK[900], lineHeight: 1.2 }}>
              Assistente Janusos.ai
            </div>
            <div
              style={{
                fontSize: 11,
                color: INK[500],
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: '#10B981',
                  boxShadow: '0 0 0 3px rgba(16,185,129,0.18)',
                  display: 'inline-block',
                }}
              />
              Online · respostas do manual oficial
            </div>
          </div>

          {/* Resize toggle: 1× ↔ 2×. Pulses prominently whenever an
              assistant reply is on screen and the panel is in compact
              mode — stops when the user expands. */}
          <Tooltip
            title={
              isExpanded
                ? 'Reduzir para o tamanho padrão'
                : shouldPulseResize
                  ? 'Expandir o painel para o dobro do tamanho'
                  : 'Expandir para o dobro do tamanho'
            }
          >
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              aria-label={isExpanded ? 'Reduzir largura do assistente' : 'Expandir largura do assistente'}
              aria-pressed={isExpanded}
              className={
                'sa-fb-btn' + (shouldPulseResize ? ' sa-resize-pulse' : '')
              }
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: INK[500],
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 120ms ease, color 120ms ease',
              }}
            >
              {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </Tooltip>

          <Tooltip title="Nova conversa">
            <button
              type="button"
              onClick={resetConversation}
              aria-label="Iniciar nova conversa"
              className="sa-fb-btn"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: INK[500],
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 120ms ease',
              }}
            >
              <RefreshCw size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Fechar">
            <button
              type="button"
              onClick={close}
              aria-label="Fechar assistente"
              className="sa-fb-btn"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: INK[500],
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 120ms ease',
              }}
            >
              <X size={18} />
            </button>
          </Tooltip>
        </div>

        {/* Scrollable conversation */}
        <div
          ref={scrollerRef}
          role="log"
          aria-live="polite"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 16px 8px',
          }}
        >
          {messages.length === 0 ? (
            <div className="sa-msg">
              <div
                style={{
                  marginBottom: 18,
                  padding: '16px 14px',
                  borderRadius: 14,
                  background: '#FFFFFF',
                  border: `1px solid ${INK[200]}`,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)',
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 600, color: INK[900], marginBottom: 4 }}>
                  Oi, {firstName} 👋
                </div>
                <div style={{ fontSize: 13, color: INK[500], lineHeight: 1.5 }}>
                  Sou o Assistente Janusos.ai. Posso te orientar sobre prospects,
                  ICP, integrações, créditos, papéis de usuário e mais. Minhas
                  respostas vêm do manual oficial da plataforma.
                </div>
              </div>

              <div style={{ fontSize: 11, fontWeight: 600, color: INK[400], textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8, paddingLeft: 2 }}>
                Sugestões para começar
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SUGGESTIONS.map((s) => (
                  <SuggestionChip key={s} label={s} onClick={() => void onSendText(s)} />
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <MessageBubble
                key={m.id}
                messageId={m.id}
                role={m.role}
                content={m.content}
                isStreaming={m.isStreaming}
                refusalType={m.refusalType ?? null}
                feedback={m.feedback}
                onFeedback={
                  m.role === 'assistant' ? (v) => submitFeedback(m.id, v) : undefined
                }
              />
            ))
          )}
          {errorMessage ? (
            <div style={{ marginTop: 8 }}>
              <Text type="danger" style={{ fontSize: 12 }}>
                {errorMessage}
              </Text>
            </div>
          ) : null}
        </div>

        {/* Composer */}
        <div
          style={{
            borderTop: `1px solid ${INK[200]}`,
            padding: '12px 14px 14px',
            background: '#FFFFFF',
          }}
        >
          <div
            className="sa-input-wrap"
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8,
              padding: '6px 6px 6px 12px',
              border: `1px solid ${INK[200]}`,
              borderRadius: 14,
              background: '#FFFFFF',
              transition: 'border-color 120ms ease, box-shadow 120ms ease',
            }}
          >
            <TextArea
              ref={inputRef as never}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDownInput}
              autoSize={{ minRows: 1, maxRows: 5 }}
              placeholder="Pergunte ao Assistente Janusos.ai…"
              disabled={isSending}
              variant="borderless"
              aria-label="Mensagem para o Assistente Janusos.ai"
              style={{ flex: 1, padding: '6px 0', fontSize: 14, lineHeight: 1.5, resize: 'none' }}
            />
            <button
              type="button"
              className="sa-send"
              onClick={onSend}
              disabled={isSending || draft.trim().length === 0}
              aria-label="Enviar mensagem"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: 'none',
                background: `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)`,
                color: '#FFFFFF',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(99,102,241,0.25)',
                transition: 'filter 120ms ease, transform 120ms ease',
              }}
            >
              <Send size={16} />
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              padding: '0 2px',
            }}
          >
            <Text style={{ fontSize: 11, color: INK[400] }}>
              Enter envia · Shift+Enter quebra linha
            </Text>
            <Text style={{ fontSize: 11, color: INK[400] }}>
              ⌘/Ctrl + /
            </Text>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default SupportAssistantWidget;
